/**
 * FitTrackHeader - Header component for the FitTrack page
 * Mobile-first responsive design with professional UX
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  List,
  Plus,
  Home,
  FileSpreadsheet,
  TrendingUp,
  Dumbbell,
  CalendarDays,
  Users,
  MessageSquarePlus,
  Eye,
  Menu,
  X,
  Search,
} from 'lucide-react';
import { useFitnessStore } from '../../stores/fitnessStore';
import { useCoachStore } from '../../stores/coachStore';
import ClientSwitcher from './ClientSwitcher';

// Calendar view mode labels
const CALENDAR_VIEW_MODES = [
  { id: 'daily', label: 'Günlük', shortLabel: 'G' },
  { id: 'weekly', label: 'Haftalık', shortLabel: 'H' },
  { id: 'monthly', label: 'Aylık', shortLabel: 'A' },
];

const FitTrackHeader = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const {
    currentMonth,
    currentWeekStart,
    selectedDay,
    goToPreviousPeriod,
    goToNextPeriod,
    goToToday,
    goToPreviousWeek,
    goToNextWeek,
    goToPreviousDay,
    goToNextDay,
    goToTodayWeek,
    viewMode,
    setViewMode,
    calendarViewMode,
    setCalendarViewMode,
    workouts,
    scheduledWorkouts,
    isGoogleConnected,
    openAddWorkoutModal,
    openConnectSheetsModal,
    openScheduleWorkoutModal,
    openExerciseSearchModal,
    formatDate,
    showAllClientsCalendar,
    toggleShowAllClientsCalendar,
  } = useFitnessStore();

  const {
    viewingClientId,
    viewingClientInfo,
    openAddNoteModal,
    isActingAsCoach,
  } = useCoachStore();

  // Calculate period-specific stats from loaded workouts
  const periodStats = useMemo(() => {
    const uniqueDates = new Set(workouts.map(w => w.date));
    return {
      workoutDays: uniqueDates.size,
      totalWorkouts: workouts.length,
    };
  }, [workouts]);

  // Generate period label based on calendar view mode
  const getPeriodLabel = (short = false) => {
    if (calendarViewMode === 'daily') {
      const date = new Date(selectedDay);
      // Header selector shows only day and month: "05 Ocak"
      if (short) {
        return format(date, 'dd MMM', { locale: tr });
      }
      return format(date, 'dd MMMM', { locale: tr });
    } else if (calendarViewMode === 'weekly') {
      const weekEnd = addDays(currentWeekStart, 6);
      if (short) {
        return `${format(currentWeekStart, 'd', { locale: tr })} - ${format(weekEnd, 'd MMM', { locale: tr })}`;
      }
      return `${format(currentWeekStart, 'd', { locale: tr })} - ${format(weekEnd, 'd MMMM yyyy', { locale: tr })}`;
    } else if (calendarViewMode === 'yearly') {
      return format(currentMonth, 'yyyy');
    } else if (calendarViewMode === 'quarterly') {
      const endMonth = new Date(currentMonth);
      endMonth.setMonth(endMonth.getMonth() + 2);
      if (short) {
        return `${format(currentMonth, 'MMM', { locale: tr })} - ${format(endMonth, 'MMM', { locale: tr })}`;
      }
      return `${format(currentMonth, 'MMM', { locale: tr })} - ${format(endMonth, 'MMM yyyy', { locale: tr })}`;
    }
    if (short) {
      return format(currentMonth, 'MMM yyyy', { locale: tr });
    }
    return format(currentMonth, 'MMMM yyyy', { locale: tr });
  };

  // Navigation handlers based on view mode
  const handlePrevious = () => {
    if (calendarViewMode === 'daily') goToPreviousDay();
    else if (calendarViewMode === 'weekly') goToPreviousWeek();
    else goToPreviousPeriod();
  };

  const handleNext = () => {
    if (calendarViewMode === 'daily') goToNextDay();
    else if (calendarViewMode === 'weekly') goToNextWeek();
    else goToNextPeriod();
  };

  const handleToday = () => {
    if (calendarViewMode === 'daily' || calendarViewMode === 'weekly') {
      goToTodayWeek();
    } else {
      goToToday();
    }
  };

  const periodLabel = getPeriodLabel();
  const shortPeriodLabel = getPeriodLabel(true);

  return (
    <header className="sticky top-0 z-40 bg-gray-950/95 backdrop-blur-xl border-b border-gray-800">
      {/* Coach viewing mode banner */}
      {viewingClientId && (
        <div className="bg-blue-600/20 border-b border-blue-500/30 px-3 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-xs sm:text-sm">
            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-400" />
            <span className="text-blue-300 truncate">
              <span className="font-medium">{viewingClientInfo?.display_name || 'Öğrenci'}</span>
              <span className="text-blue-400/70 hidden xs:inline">'nin antrenmanlarını görüntülüyorsunuz</span>
            </span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Main header row */}
        <div className="flex items-center justify-between h-14 gap-2">
          {/* Left - Home & Period Navigation (always visible) */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            {/* Home button - visible on larger screens */}
            <button
              onClick={() => navigate('/')}
              className="hidden sm:flex p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
              title="Ana Sayfa"
            >
              <Home className="w-5 h-5" />
            </button>

            {/* Client Switcher - only shown for coaches, hidden on very small screens */}
            {isActingAsCoach && (
              <div className="hidden xs:block flex-shrink-0">
                <ClientSwitcher />
              </div>
            )}

            {/* Show All Clients Toggle - only in "Kendim" mode for coaches */}
            {isActingAsCoach && !viewingClientId && (
              <button
                onClick={toggleShowAllClientsCalendar}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all flex-shrink-0 ${
                  showAllClientsCalendar
                    ? 'bg-violet-500/20 text-violet-400 border border-violet-500/50'
                    : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'
                }`}
                title={showAllClientsCalendar ? 'Sadece kendi takvimimi göster' : 'Tüm öğrencileri göster'}
              >
                <Users className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{showAllClientsCalendar ? 'Tümü' : 'Sadece Ben'}</span>
              </button>
            )}

            {/* Period Navigation - always visible, touch-friendly */}
            <div className="flex items-center bg-gray-800/50 rounded-lg p-0.5 flex-shrink-0">
              <button
                onClick={handlePrevious}
                className="p-2.5 sm:p-2 text-gray-400 hover:text-white active:bg-gray-700 hover:bg-gray-700 rounded-md transition-colors min-h-touch min-w-touch flex items-center justify-center"
                aria-label="Önceki dönem"
              >
                <ChevronLeft className="w-5 h-5 sm:w-4 sm:h-4" />
              </button>

              <button
                onClick={handleToday}
                className="px-3 sm:px-4 py-2.5 sm:py-2 text-xs sm:text-sm font-medium text-white hover:bg-gray-700 active:bg-gray-600 rounded-md transition-colors text-center capitalize min-w-0 min-h-touch"
                aria-label="Bugüne git"
              >
                <span className="hidden sm:inline">{periodLabel}</span>
                <span className="sm:hidden">{shortPeriodLabel}</span>
              </button>

              <button
                onClick={handleNext}
                className="p-2.5 sm:p-2 text-gray-400 hover:text-white active:bg-gray-700 hover:bg-gray-700 rounded-md transition-colors min-h-touch min-w-touch flex items-center justify-center"
                aria-label="Sonraki dönem"
              >
                <ChevronRight className="w-5 h-5 sm:w-4 sm:h-4" />
              </button>
            </div>

            {/* Calendar view mode selector - desktop only */}
            {viewMode === 'calendar' && (
              <div className="hidden lg:flex bg-gray-800/50 rounded-lg p-1 flex-shrink-0">
                {CALENDAR_VIEW_MODES.map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => setCalendarViewMode(mode.id)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      calendarViewMode === mode.id
                        ? 'bg-orange-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Center - Period Stats - desktop only */}
          <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full">
              <CalendarDays className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-blue-400">{periodStats.workoutDays}</span>
              <span className="text-xs text-blue-400/70">gün</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-full">
              <Dumbbell className="w-4 h-4 text-green-400" />
              <span className="text-sm font-semibold text-green-400">{periodStats.totalWorkouts}</span>
              <span className="text-xs text-green-400/70">antrenman</span>
            </div>
          </div>

          {/* Right - Actions */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Desktop action buttons */}
            <div className="hidden md:flex items-center gap-1 bg-gray-800/50 rounded-lg p-1">
              <button
                onClick={openExerciseSearchModal}
                className="p-2 text-gray-400 hover:text-orange-400 hover:bg-gray-700 rounded-md transition-colors"
                title="Egzersiz Ara"
              >
                <Search className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/coaches')}
                className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-md transition-colors"
                title="Koçlarım"
              >
                <Users className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/fittrack/analytics')}
                className="p-2 text-gray-400 hover:text-purple-400 hover:bg-gray-700 rounded-md transition-colors"
                title="Analiz & İstatistikler"
              >
                <TrendingUp className="w-4 h-4" />
              </button>
              <button
                onClick={openConnectSheetsModal}
                className={`p-2 rounded-md transition-colors ${
                  isGoogleConnected
                    ? 'text-green-400 hover:bg-gray-700'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
                title={isGoogleConnected ? 'Google Sheets bağlı' : 'Google Sheets bağla'}
              >
                <FileSpreadsheet className="w-4 h-4" />
              </button>
              <div className="w-px h-5 bg-gray-700 mx-0.5" />
              <button
                onClick={() => setViewMode('calendar')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-orange-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
                title="Takvim Görünümü"
              >
                <Calendar className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-orange-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
                title="Liste Görünümü"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Add note button - only shown when viewing a client (desktop) */}
            {viewingClientId && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => openAddNoteModal({ clientId: viewingClientId })}
                className="hidden md:flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-lg shadow-blue-600/20"
              >
                <MessageSquarePlus className="w-4 h-4" />
                <span className="hidden lg:inline">Not Ekle</span>
              </motion.button>
            )}

            {/* Schedule workout button - always visible */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => openScheduleWorkoutModal()}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-orange-600 hover:bg-orange-500 active:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors shadow-lg shadow-orange-600/20"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Antrenman Planla</span>
            </motion.button>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2.5 text-gray-400 hover:text-white active:bg-gray-800 rounded-lg transition-colors"
              aria-label="Menüyü aç"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-gray-800 bg-gray-900/95 backdrop-blur-xl overflow-hidden"
          >
            <div className="px-4 py-3 space-y-3">
              {/* Search Button */}
              <button
                onClick={() => {
                  openExerciseSearchModal();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600/20 text-orange-400 border border-orange-500/30 rounded-lg font-medium transition-colors active:bg-orange-600/30"
              >
                <Search className="w-4 h-4" />
                <span className="text-sm">Egzersiz Ara</span>
              </button>

              {/* View Mode Toggle - Calendar/List */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setViewMode('calendar');
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    viewMode === 'calendar'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-800 text-gray-400 active:bg-gray-700'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Takvim</span>
                </button>
                <button
                  onClick={() => {
                    setViewMode('list');
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    viewMode === 'list'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-800 text-gray-400 active:bg-gray-700'
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span className="text-sm">Liste</span>
                </button>
              </div>

              {/* Calendar View Mode - only show in calendar mode */}
              {viewMode === 'calendar' && (
                <div className="flex gap-1.5">
                  {CALENDAR_VIEW_MODES.map((mode) => (
                    <button
                      key={mode.id}
                      onClick={() => {
                        setCalendarViewMode(mode.id);
                      }}
                      className={`flex-1 px-2 py-2 text-xs font-medium rounded-lg transition-colors ${
                        calendarViewMode === mode.id
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-800/50 text-gray-500 active:bg-gray-700'
                      }`}
                    >
                      {mode.shortLabel}
                    </button>
                  ))}
                </div>
              )}

              {/* Stats Row */}
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <CalendarDays className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-semibold text-blue-400">{periodStats.workoutDays}</span>
                  <span className="text-2xs text-blue-400/70">gün</span>
                </div>
                <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <Dumbbell className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-semibold text-green-400">{periodStats.totalWorkouts}</span>
                  <span className="text-2xs text-green-400/70">antrenman</span>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={() => {
                    navigate('/');
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex flex-col items-center gap-1 px-2 py-3 bg-gray-800 text-gray-400 rounded-lg active:bg-gray-700 transition-colors"
                >
                  <Home className="w-5 h-5" />
                  <span className="text-2xs">Ana Sayfa</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/coaches');
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex flex-col items-center gap-1 px-2 py-3 bg-gray-800 text-gray-400 rounded-lg active:bg-gray-700 transition-colors"
                >
                  <Users className="w-5 h-5" />
                  <span className="text-2xs">Koçlarım</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/fittrack/analytics');
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex flex-col items-center gap-1 px-2 py-3 bg-gray-800 text-gray-400 rounded-lg active:bg-gray-700 transition-colors"
                >
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-2xs">Analiz</span>
                </button>
                <button
                  onClick={() => {
                    openConnectSheetsModal();
                    setIsMobileMenuOpen(false);
                  }}
                  className={`flex flex-col items-center gap-1 px-2 py-3 rounded-lg transition-colors ${
                    isGoogleConnected
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-800 text-gray-400 active:bg-gray-700'
                  }`}
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  <span className="text-2xs">{isGoogleConnected ? 'Bağlı' : 'Sheets'}</span>
                </button>
              </div>

              {/* Schedule/Note Actions */}
              <div className="flex gap-2 pt-1">
                {/* Schedule workout - always visible */}
                <button
                  onClick={() => {
                    openScheduleWorkoutModal();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 text-white rounded-lg active:bg-orange-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">Antrenman Planla</span>
                </button>
                {/* Add note - when viewing client */}
                {viewingClientId && (
                  <button
                    onClick={() => {
                      openAddNoteModal({ clientId: viewingClientId });
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg active:bg-blue-700 transition-colors"
                  >
                    <MessageSquarePlus className="w-4 h-4" />
                    <span className="text-sm font-medium">Not Ekle</span>
                  </button>
                )}
              </div>

              {/* Client Switcher for very small screens (if coach) */}
              {isActingAsCoach && (
                <div className="xs:hidden pt-2 border-t border-gray-800">
                  <ClientSwitcher />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default FitTrackHeader;
